<template>
  <div v-if="expedient" class="expedient-viewer">
    <!-- Header mejorado del expediente -->
    <div class="hero is-bold mb-5">
      <div class="hero-body py-4">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <div class="media">
                <div class="media-left">
                  <figure class="image is-64x64">
                    <div class="expedient-type-badge">
                      <span
                        class="icon is-large"
                        :style="{ color: expedient.expedient_type.color }"
                      >
                        <i
                          :class="expedient.expedient_type.icon"
                          class="fa-2x"
                        ></i>
                      </span>
                    </div>
                  </figure>
                </div>
                <div class="media-content">
                  <div class="title is-3 mb-1">{{ expedient.name }}</div>
                  <div class="subtitle is-6 mb-2" v-if="expedient.description">
                    {{ expedient.description }}
                  </div>
                  <div
                    class="subtitle is-6 mb-2"
                    v-if="expedient.expedient_number"
                  >
                    <span class="has-text-grey"
                      >{{ $t("archive.expedient number") }}:</span
                    >
                    <strong class="has-text-info ml-1"
                      >#{{ expedient.expedient_number }}</strong
                    >
                  </div>
                  <div class="tags">
                    <span
                      class="tag is-medium has-text-white"
                      :style="{
                        backgroundColor: expedient.expedient_type.color,
                      }"
                    >
                      <span class="icon">
                        <i :class="expedient.expedient_type.icon"></i>
                      </span>
                      <span>{{ expedient.expedient_type.name }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <div class="field is-grouped">
                <div class="control">
                  <div class="tags has-addons">
                    <span class="tag is-dark">{{ $t("archive.status") }}</span>
                    <span
                      class="tag"
                      :class="{
                        'is-success': expedient.status === 'active',
                        'is-warning': expedient.status === 'pending',
                        'is-danger': expedient.status === 'closed',
                        'is-info': expedient.status === 'archived',
                      }"
                    >
                      <span class="icon is-small">
                        <i
                          :class="{
                            'fa fa-check': expedient.status === 'active',
                            'fa fa-clock': expedient.status === 'pending',
                            'fa fa-times': expedient.status === 'closed',
                            'fa fa-archive': expedient.status === 'archived',
                          }"
                        ></i>
                      </span>
                      <span>{{
                        $t(`archive.status ${expedient.status}`)
                      }}</span>
                    </span>
                  </div>
                </div>
                <div class="control">
                  <div class="tags has-addons">
                    <span class="tag is-dark">{{
                      $t("archive.priority")
                    }}</span>
                    <span
                      class="tag"
                      :class="{
                        'is-dark': expedient.priority === 'low',
                        'is-warning': expedient.priority === 'medium',
                        'is-danger': expedient.priority === 'high',
                        'is-black': expedient.priority === 'urgent',
                      }"
                    >
                      <span class="icon is-small">
                        <i
                          :class="{
                            'fa fa-chevron-down': expedient.priority === 'low',
                            'fa fa-minus': expedient.priority === 'medium',
                            'fa fa-chevron-up': expedient.priority === 'high',
                            'fa fa-exclamation':
                              expedient.priority === 'urgent',
                          }"
                        ></i>
                      </span>
                      <span>{{
                        $t(`archive.priority ${expedient.priority}`)
                      }}</span>
                    </span>
                  </div>
                </div>
              </div>
              <div class="buttons are-small is-pulled-right">
                <button class="button is-primary">
                  <span class="icon">
                    <i class="fa fa-upload"></i>
                  </span>
                  <span>{{ $t("archive.upload files") }}</span>
                </button>
                <button class="button is-info" @click="edit_expedient">
                  <span class="icon">
                    <i class="fa fa-edit"></i>
                  </span>
                  <span>{{ $t("edit") }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sección de archivos -->
    <div
      class="card mt-5"
      v-if="expedient.archive_files && expedient.archive_files.length > 0"
    >
      <header class="card-header">
        <p class="card-header-title">
          <span class="icon has-text-primary">
            <i class="fa fa-folder-open"></i>
          </span>
          <span>{{ $t("archive.attached files") }}</span>
          <span class="tag is-primary is-rounded ml-2">{{
            expedient.archive_files.length
          }}</span>
        </p>
        <!-- <button class="card-header-icon">
          <span class="icon">
            <i class="fa fa-angle-down"></i>
          </span>
        </button> -->
      </header>
      <div class="card-content p-0">
        <div class="file-list">
          <div
            class="file-item"
            v-for="(file, index) in expedient.archive_files"
            :key="index"
          >
            <div
              class="media p-4"
              :class="{ 'has-background-dark-ter': index % 2 === 0 }"
            >
              <div class="media-left">
                <div class="file-icon-wrapper">
                  <span
                    class="icon is-large"
                    :class="getFileIconColor(file.drive_file.settings.mimetype)"
                  >
                    <i
                      :class="getFileIcon(file.drive_file.settings.mimetype)"
                      class="fa-2x"
                    ></i>
                  </span>
                </div>
              </div>
              <div class="media-content">
                <div class="content">
                  <div class="level">
                    <div class="level-left">
                      <div class="level-item">
                        <div>
                          <p class="title is-6 mb-1">
                            {{ file.drive_file.name }}
                          </p>
                          <div class="tags">
                            <span class="tag is-dark">
                              <span class="icon is-small">
                                <i class="fa fa-hdd-o"></i>
                              </span>
                              <span>{{
                                formatFileSize(file.drive_file.settings.size)
                              }}</span>
                            </span>
                            <span
                              v-if="file.category"
                              class="tag is-small has-text-white"
                              :style="{ backgroundColor: file.category.color }"
                            >
                              <span class="icon is-small">
                                <i :class="file.category.icon"></i>
                              </span>
                              <span>{{ file.category.name }}</span>
                            </span>
                            <span v-else class="tag is-small is-dark">
                              {{ $t("archive.no category") }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="level-right">
                      <div class="level-item">
                        <div class="field is-grouped">
                          <div class="control">
                            <button
                              class="button is-small is-outlined is-primary"
                            >
                              <span class="icon is-small">
                                <i class="fa fa-eye"></i>
                              </span>
                              <span>{{ $t("archive.preview") }}</span>
                            </button>
                          </div>
                          <div class="control">
                            <button class="button is-small is-outlined is-info">
                              <span class="icon is-small">
                                <i class="fa fa-download"></i>
                              </span>
                              <span>{{ $t("archive.download") }}</span>
                            </button>
                          </div>
                          <div class="control">
                            <div class="dropdown is-hoverable">
                              <div class="dropdown-trigger">
                                <button class="button is-small is-outlined">
                                  <span class="icon is-small">
                                    <i class="fa fa-ellipsis-v"></i>
                                  </span>
                                </button>
                              </div>
                              <div class="dropdown-menu">
                                <div class="dropdown-content">
                                  <a href="#" class="dropdown-item">
                                    <span class="icon is-small">
                                      <i class="fa fa-edit"></i>
                                    </span>
                                    <span>{{ $t("edit") }}</span>
                                  </a>
                                  <a href="#" class="dropdown-item">
                                    <span class="icon is-small">
                                      <i class="fa fa-share"></i>
                                    </span>
                                    <span>{{ $t("archive.share") }}</span>
                                  </a>
                                  <hr class="dropdown-divider" />
                                  <a
                                    href="#"
                                    class="dropdown-item has-text-danger"
                                  >
                                    <span class="icon is-small">
                                      <i class="fa fa-trash"></i>
                                    </span>
                                    <span>{{ $t("delete") }}</span>
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Estado vacío cuando no hay archivos -->
    <div v-else class="empty-state-card">
      <div class="card mt-5">
        <div class="card-content has-text-centered py-6">
          <div class="empty-illustration mb-4">
            <span class="icon is-large has-text-grey-dark">
              <i class="fa fa-folder-open fa-4x"></i>
            </span>
          </div>
          <h3 class="title is-5 has-text-grey">
            {{ $t("archive.no files in expedient") }}
          </h3>
          <p class="subtitle is-6 has-text-grey-dark mb-5">
            {{ $t("archive.upload first file message") }}
          </p>
          <button class="button is-primary is-medium">
            <span class="icon">
              <i class="fa fa-upload"></i>
            </span>
            <span>{{ $t("archive.upload first file") }}</span>
          </button>
        </div>
      </div>
    </div>
    <expedient-modal ref="expedient_modal" />
  </div>

  <!-- Estado de selección inicial -->
  <div v-else class="selection-state">
    <div class="hero is-dark is-fullheight-with-navbar">
      <div class="hero-body">
        <div class="container has-text-centered">
          <div class="empty-illustration mb-5">
            <span class="icon is-large has-text-grey-darker">
              <i class="fa fa-folder-open fa-5x"></i>
            </span>
          </div>
          <h1 class="title is-3 has-text-grey">
            {{ $t("archive.select an expedient") }}
          </h1>
          <p class="subtitle is-5 has-text-grey-dark">
            {{ $t("archive.click expedient to view details") }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get, error_message } from "@/utils";
import ExpedientModal from "../expedients_datatable/expedient_modal/Index.vue";

export default {
  name: "ExpedientViewer",
  components: { ExpedientModal },
  props: {
    // expedient: {
    //   type: Object,
    //   required: true,
    // },
  },
  data() {
    return {
      expedient: null,
    };
  },
  computed: {},
  mounted() {},
  methods: {
    set_expedient(uuid) {
      get("/archive/expedient/detail", { uuid })
        .then((data) => {
          this.expedient = data;
        })
        .catch((error) => {
          error_message(this.$t("error loading expedient details"));
        });
    },

    formatDate(dateString) {
      if (!dateString) return "";

      const date = new Date(dateString);
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };

      return date.toLocaleDateString(this.$i18n.locale, options);
    },

    formatFileSize(bytes) {
      if (!bytes) return "0 B";

      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    getFileIcon(mimetype) {
      if (!mimetype) return "fa fa-file-o";

      if (mimetype.includes("pdf")) return "fa fa-file-pdf-o";
      if (mimetype.includes("word") || mimetype.includes("document"))
        return "fa fa-file-word-o";
      if (mimetype.includes("excel") || mimetype.includes("spreadsheet"))
        return "fa fa-file-excel-o";
      if (mimetype.includes("powerpoint") || mimetype.includes("presentation"))
        return "fa fa-file-powerpoint-o";
      if (mimetype.includes("image")) return "fa fa-file-image-o";
      if (mimetype.includes("video")) return "fa fa-file-video-o";
      if (mimetype.includes("audio")) return "fa fa-file-audio-o";
      if (mimetype.includes("text")) return "fa fa-file-text-o";
      if (
        mimetype.includes("zip") ||
        mimetype.includes("rar") ||
        mimetype.includes("compressed")
      )
        return "fa fa-file-archive-o";

      return "fa fa-file-o";
    },

    getFileIconColor(mimetype) {
      if (!mimetype) return "has-text-grey";

      if (mimetype.includes("pdf")) return "has-text-danger";
      if (mimetype.includes("word") || mimetype.includes("document"))
        return "has-text-info";
      if (mimetype.includes("excel") || mimetype.includes("spreadsheet"))
        return "has-text-success";
      if (mimetype.includes("powerpoint") || mimetype.includes("presentation"))
        return "has-text-warning";
      if (mimetype.includes("image")) return "has-text-primary";
      if (mimetype.includes("video")) return "has-text-dark";
      if (mimetype.includes("audio")) return "has-text-link";
      if (mimetype.includes("text")) return "has-text-grey-dark";
      if (
        mimetype.includes("zip") ||
        mimetype.includes("rar") ||
        mimetype.includes("compressed")
      )
        return "has-text-warning-dark";

      return "has-text-grey";
    },
    edit_expedient() {
      this.$refs.expedient_modal.show_modal(this.expedient.uuid);
    },
  },
  watch: {},
};
</script>

<style scoped>
/* Componente principal */
.expedient-viewer {
  min-height: 100vh;
}

/* Badge del tipo de expediente */
.expedient-type-badge {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 2px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Avatar del cliente */
.customer-avatar {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Iconos de estadísticas */
.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Lista de archivos mejorada */
.file-list {
  max-height: 600px;
  overflow-y: auto;
}

.file-item {
  border-bottom: 1px solid #828282;
  transition: all 0.3s ease;
}

.file-item:last-child {
  border-bottom: none;
}

.file-item:hover {
  background-color: #3a3a3a !important;
  transform: translateX(4px);
}

.file-icon-wrapper {
  position: relative;
}

.file-icon-wrapper::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  z-index: -1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

/* Estados vacíos mejorados */
.empty-illustration {
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
}

.empty-state-card .card {
  border: 2px dashed #dbdbdb;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
}

.selection-state {
  min-height: calc(100vh - 200px);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Mejoras en botones */
.button {
  transition: all 0.3s ease;
}

.button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Mejoras en tarjetas */
.card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

/* Hero mejorado */
.hero {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Animaciones suaves para tags */
.tag {
  transition: all 0.3s ease;
}

.tags .tag:hover {
  transform: scale(1.05);
}

/* Dropdown mejorado */
.dropdown-menu {
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  border: none;
}

.dropdown-item {
  transition: all 0.2s ease;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
  transform: translateX(4px);
}

.level-right .level-item {
  display: block;
}

/* Responsivo */
@media screen and (max-width: 768px) {
  .expedient-viewer .hero-body {
    padding: 1.5rem;
  }

  .expedient-viewer .columns {
    margin: 0;
  }

  .expedient-viewer .column {
    padding: 0.75rem;
  }

  .file-list {
    max-height: 400px;
  }

  .level-right .field.is-grouped {
    flex-direction: column;
    align-items: stretch;
  }

  .level-right .control {
    margin-bottom: 0.5rem;
  }
}

/* Scrollbar personalizado */
.file-list::-webkit-scrollbar {
  width: 6px;
}

.file-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.file-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.file-list::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}
</style>
